import { type Unsubscribe } from '../utils/Unsubscribe';

/** Receives the payload of one event type. */
export type EventListener<TPayload> = (payload: TPayload) => void;

/**
 * Constrains an event map: any object type whose keys name events and whose values are their payloads.
 *
 * Deliberately `object` rather than `Record<string, unknown>` so an `interface` works as an event map,
 * not only a type alias. Only string keys are ever used, which the emitter's signatures enforce.
 */
export type EventMap = object;

interface Registration<TPayload> {
  readonly listener: EventListener<TPayload>;
  readonly once: boolean;
}

/**
 * A typed publish and subscribe hub.
 *
 * The type parameter is a map from event name to payload type, so `emit` and `on` agree at compile
 * time and a renamed event breaks the build rather than going quietly unheard. Emitters are ordinary
 * objects owned by whoever creates them, never a global bus.
 */
export class EventEmitter<TEventMap extends EventMap> {
  readonly #registrations = new Map<keyof TEventMap & string, Registration<never>[]>();

  /**
   * Subscribes to an event and returns the function that cancels the subscription.
   *
   * Registering the same listener twice registers it twice; it will be called twice.
   */
  on<TType extends keyof TEventMap & string>(type: TType, listener: EventListener<TEventMap[TType]>): Unsubscribe {
    return this.#add(type, listener, false);
  }

  /** Subscribes to the next occurrence of an event only. */
  once<TType extends keyof TEventMap & string>(type: TType, listener: EventListener<TEventMap[TType]>): Unsubscribe {
    return this.#add(type, listener, true);
  }

  /** Removes the first registration matching this listener, and reports whether one was found. */
  off<TType extends keyof TEventMap & string>(type: TType, listener: EventListener<TEventMap[TType]>): boolean {
    return this.#remove(type, listener);
  }

  /**
   * Delivers a payload to every current listener, in registration order.
   *
   * Listeners are snapshotted first, so subscribing or unsubscribing from inside a listener affects
   * the next emit rather than corrupting this one.
   */
  emit<TType extends keyof TEventMap & string>(type: TType, payload: TEventMap[TType]): void {
    const registrations = this.#registrations.get(type);
    if (registrations === undefined) {
      return;
    }

    for (const registration of [...registrations]) {
      if (registration.once) {
        this.#remove(type, registration.listener);
      }
      (registration.listener as EventListener<TEventMap[TType]>)(payload);
    }
  }

  /** Number of listeners currently registered for an event. */
  listenerCount(type: keyof TEventMap & string): number {
    return this.#registrations.get(type)?.length ?? 0;
  }

  /** Removes every listener for one event, or for all events when no type is given. */
  removeAllListeners(type?: keyof TEventMap & string): void {
    if (type === undefined) {
      this.#registrations.clear();
      return;
    }
    this.#registrations.delete(type);
  }

  #add<TType extends keyof TEventMap & string>(
    type: TType,
    listener: EventListener<TEventMap[TType]>,
    once: boolean,
  ): Unsubscribe {
    let registrations = this.#registrations.get(type);
    if (registrations === undefined) {
      registrations = [];
      this.#registrations.set(type, registrations);
    }
    registrations.push({ listener, once });

    return () => {
      this.#remove(type, listener);
    };
  }

  #remove(type: keyof TEventMap & string, listener: EventListener<never>): boolean {
    const registrations = this.#registrations.get(type);
    if (registrations === undefined) {
      return false;
    }

    const index = registrations.findIndex((registration) => registration.listener === listener);
    if (index < 0) {
      return false;
    }

    registrations.splice(index, 1);
    if (registrations.length === 0) {
      this.#registrations.delete(type);
    }
    return true;
  }
}
